/** @format */

import React, { useEffect } from 'react';
import { Row, Col, Select } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import backArrow from 'img/ArrowLeft.svg';
import { setUserId } from 'state/slice/dropDownSlice';
import { useFormData } from '../hooks/useFormData';
import { useApiData } from '../hooks/useApiData';
import { submitSaleForm } from '../services/apiService';
import { ENROLLMENT_FEES } from '../types';
import {
  PlanTypeSelection,
  MemberPreliminaryInfo,
  MemberInformation,
  AddressForm,
  UserSelection,
  SpouseForm,
  DependentsList,
  AncillaryList,
  MajorMedForm,
  PrivateMedForm,
  BackOfficeInfo,
  QLESelection,
} from '../components';

const { Option } = Select;

interface SaleSubmissionFormContainerProps {
  hideForm: () => void;
  showUserSelection?: boolean;
}

export const SaleSubmissionFormContainer: React.FC<SaleSubmissionFormContainerProps> = ({
  hideForm,
  showUserSelection = false,
}) => {
  const dispatch = useDispatch();
  const selectedUserId = useSelector((state: any) => state.dropDown.userId);
  const currentUser = useSelector((state: any) => state.user.userData);
  
  // Custom hooks for data management
  const { loading, error } = useApiData();
  const {
    formData,
    generalQ,
    handleChange,
    handleGeneralQChange,
    handleDependentChange,
    addDependent,
    removeDependent,
    addAncillary,
    removeAncillary,
    setFormData,
    resetForm,
  } = useFormData();

  const handleUserChange = (userId: string | null) => {
    if (userId) {
      dispatch(setUserId({ userId }));
    } else {
      dispatch(setUserId({ userId: null }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Determine the userId for the submission
    let submissionUserId;
    if (showUserSelection) {
      // If user selection is shown (admin), use selected user or fallback to current user
      submissionUserId = selectedUserId || currentUser?._id;
    } else {
      // If user selection is not shown (regular user), use current user
      submissionUserId = currentUser?._id;
    }
    
    const submissionData = {
      ...formData,
      userId: submissionUserId,
    };

    // Debug logging
    console.log('Submission Data:', {
      showUserSelection,
      selectedUserId,
      currentUserId: currentUser?._id,
      finalUserId: submissionUserId,
    });

    if (!submissionUserId) {
      alert('Error: No user ID available for submission');
      return;
    }

    try {
      await submitSaleForm(submissionData);
      alert('Form submitted successfully');
      
      // Reset everything after successful submission
      resetForm();
      
      // Reset selected user in Redux if admin
      if (showUserSelection) {
        dispatch(setUserId({ userId: null }));
      }
    } catch (error) {
      console.error('Error submitting form', error);
      alert('Failed to submit form');
    }
  };

  // Prevent scroll on number inputs
  useEffect(() => {
    const numberInputs = document.querySelectorAll('input[type="number"]');
    const handleWheel = (event: Event) => {
      event.preventDefault();
    };

    numberInputs.forEach((input) =>
      input.addEventListener('wheel', handleWheel)
    );

    return () => {
      numberInputs.forEach((input) =>
        input.removeEventListener('wheel', handleWheel)
      );
    };
  }, []);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div className='u65-form'>
      <div style={{ marginBottom: '28px' }}>
        <button className='white-back-button' onClick={hideForm}>
          <img src={backArrow} alt='back Arrow' />
          <span>Back</span>
        </button>
      </div>

      <form onSubmit={handleSubmit} style={{ width: '100%' }}>
        <Row justify={'center'}>
          <Col sm={24} md={24} lg={16} xl={16}>
            <Row gutter={[24, 24]}>
              <Col sm={24} md={24} lg={24} xl={24}>
                <div className='form-title'>
                  <span>Sales Submission Form</span>
                </div>
              </Col>

              {/* User Selection - Admin Only */}
              {showUserSelection && (
                <UserSelection
                  onUserChange={handleUserChange}
                  selectedUserId={selectedUserId}
                />
              )}

              {/* Member Preliminary Information */}
              <MemberPreliminaryInfo
                request={formData.request}
                dependents={generalQ.dependents}
                status={generalQ.status}
                leadId={formData.leadId || ''}
                onRequestChange={handleChange}
                onDependentsChange={handleGeneralQChange}
                onStatusChange={handleGeneralQChange}
                onLeadIdChange={handleChange}
              />

              {/* Plan Type Selection */}
              <PlanTypeSelection
                selectedPlanTypes={formData.planType || []}
                isAcaRequest={formData.request}
                onChange={handleChange}
              />

              {/* Member Information */}
              <MemberInformation
                formData={formData}
                onChange={handleChange}
                showAcaFields={formData.request}
              />

              {/* QLE Selection - Only for ACA Requests, comes after Member Info */}
              {formData.request && (
                <QLESelection
                  qle={formData.QLE}
                  onChange={handleChange}
                />
              )}

              {/* Member ID - Conditional on Private Med or Ancillary */}
              {(formData.planType?.includes('Private Med') || formData.planType?.includes('Ancillary')) && (
                <Col sm={24} md={12} lg={12} xl={12}>
                  <div className='form-input-wrapper'>
                    <label style={{ marginBottom: '10px' }}>Member Id *</label>
                    <input
                      name='memberId'
                      value={formData.memberId}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </Col>
              )}

              {/* FFM ID - Conditional on Major Medical and NOT ACA Request */}
              {formData.planType?.includes('Major Medical') && !formData.request && (
                <Col sm={24} md={12} lg={12} xl={12}>
                  <div className='form-input-wrapper'>
                    <label style={{ marginBottom: '10px' }}>FFM Id *</label>
                    <input
                      name='FFMId'
                      value={formData.FFMId || ''}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </Col>
              )}

              {/* Address Form - Only for ACA Requests */}
              {formData.request && (
                <Col sm={24} md={24} lg={24} xl={24}>
                  <AddressForm
                    address={formData.address || { street: '', aptUnite: '', city: '', state: '', zipCode: '' }}
                    onChange={handleChange}
                  />
                </Col>
              )}

              {/* Spouse Information */}
              {generalQ.status === 'married' && (
                <SpouseForm
                  spouseInfo={formData.spouseInfo || { firstName: '', lastName: '', dob: '', ssn: '' }}
                  onChange={handleChange}
                />
              )}

              {/* Dependents List */}
              {generalQ.dependents && (
                <DependentsList
                  dependents={formData.dependentsInfo || []}
                  onAdd={addDependent}
                  onRemove={removeDependent}
                  onChange={handleDependentChange}
                />
              )}

              {/* Private Medical Form - Appears First */}
              {formData.planType?.includes('Private Med') && (
                <PrivateMedForm
                  data={formData.privateMed || { carrier: '', isPostDate: null, billingDate: '', premium: '', status: '' }}
                  onChange={(field, value) =>
                    setFormData({
                      ...formData,
                      privateMed: { ...(formData.privateMed || {}), [field]: value },
                    })
                  }
                />
              )}

              {/* Major Medical Form - Appears Second */}
              {formData.planType?.includes('Major Medical') && (
                <MajorMedForm
                  data={formData.majorMed || { carrier: '', isPostDate: null, billingDate: '', premium: '', status: '' }}
                  isAcaRequest={formData.request}
                  onChange={(field, value) => 
                    setFormData({
                      ...formData,
                      majorMed: { ...(formData.majorMed || {}), [field]: value },
                    })
                  }
                />
              )}

              {/* Ancillary List - Appears Third */}
              {formData.planType?.includes('Ancillary') && (
                <AncillaryList
                  ancillaries={formData.ancillaries || []}
                  onAdd={addAncillary}
                  onRemove={removeAncillary}
                  onChange={(index, field, value) => {
                    const currentAncillaries = formData.ancillaries || [];
                    const newAncillaries = [...currentAncillaries];
                    newAncillaries[index] = {
                      ...newAncillaries[index],
                      [field]: value,
                    };
                    setFormData({ ...formData, ancillaries: newAncillaries });
                  }}
                />
              )}

              {/* Back Office Information */}
              <BackOfficeInfo
                aorId={formData.AOR}
                npnId={formData.NPN}
                sherpa={formData.healthSherpa}
                planTypes={formData.planType || []}
                isAcaRequest={formData.request}
                onAorChange={(value) => setFormData({ ...formData, AOR: value })}
                onNpnChange={(value) => setFormData({ ...formData, NPN: value })}
                onSherpaChange={(value) => setFormData({ ...formData, healthSherpa: value })}
              />

              {/* Benefits URL - Conditional on Major Medical OR ACA Request */}
              {(formData.planType?.includes('Major Medical') || formData.request) && (
                <Col sm={24} md={24} lg={12} xl={12}>
                  <div className='input-group'>
                    <label> Benefit URL</label>
                    <input
                      style={{ width: '100%' }}
                      type='text'
                      name='benefitsURL'
                      value={formData.benefitsURL}
                      onChange={handleChange}
                    />
                  </div>
                </Col>
              )}

              {/* Enrollment Fee - Conditional on NOT Major Med */}
              {!formData.planType?.some((type) => ['Major Med'].includes(type)) && (
                <Col sm={24} md={24} lg={12} xl={12}>
                  <div className='radial-input'>
                    <label>Enrollment Fee *</label>
                    <Select
                      showSearch
                      placeholder='Select enrollement Fee'
                      optionFilterProp='children'
                      onChange={(value) => setFormData({ ...formData, enrollmentFee: value })}
                      value={formData.enrollmentFee}
                      style={{ width: '100%' }}
                    >
                      {ENROLLMENT_FEES.map((fee) => (
                        <Option key={fee} value={fee} label={fee}>
                          ${fee}
                        </Option>
                      ))}
                    </Select>
                  </div>
                </Col>
              )}

              {/* Notes - Always Shown */}
              <Col sm={24} md={24} lg={24} xl={24}>
                <div className='input-group'>
                  <label>Notes:</label>
                  <textarea
                    style={{ width: '100%', height: '300px' }}
                    name='notes'
                    value={formData.notes || ''}
                    onChange={handleChange}
                  />
                </div>
              </Col>

              {/* Submit Button */}
              <Col sm={24} md={24} lg={24} xl={24}>
                <div className='submit-submission-form'>
                  <button type='submit'>Submit Form</button>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </form>
    </div>
  );
};
